import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHero } from "../components/Layout";
import { QuoteForm } from "../components/QuoteForm";
import { useSite } from "../context/SiteContext";
import { useSeo } from "../lib/useSeo";

const Success = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} data-testid="quote-success-message" className="border border-line bg-white px-8 py-20 text-center md:px-16 md:py-28">
    <span className="mx-auto block h-3 w-3 rotate-45 border border-wine" />
    <h2 className="mt-10 font-serif text-4xl font-light leading-tight text-wine md:text-5xl">Thank you for reaching out.</h2>
    <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-charcoal/85 md:text-lg">
      Your enquiry has been received.<br />We look forward to exploring your vision.
    </p>
    <Link to="/portfolio" className="btn btn-secondary mt-12" data-testid="quote-success-portfolio-btn">Explore the Portfolio</Link>
  </motion.div>
);

export default function Contact() {
  useSeo("Request a Quote — Collaborate with BWI", "Tell us about your beauty, skincare, makeup or jewellery brand and request a custom quote for product advertising content.");
  const { settings } = useSite();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const id = requestAnimationFrame(() => {
      const el = document.querySelector("[data-testid=quote-success-message]");
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      if (window.__lenis) {
        window.__lenis.resize();
        window.__lenis.scrollTo(top, { immediate: true, force: true });
      } else window.scrollTo(0, top);
    });
    return () => cancelAnimationFrame(id);
  }, [done]);
  const c = settings?.contact || {};
  const s = settings?.socials || {};
  const details = [
    ["Email", c.email, c.email && `mailto:${c.email}`],
    ["Phone", c.phone, c.phone && `tel:${c.phone.replace(/\s/g, "")}`],
    ["WhatsApp", c.whatsapp, c.whatsapp && `https://wa.me/${c.whatsapp.replace(/\D/g, "")}`],
    ["Instagram", s.instagram && "Follow the studio", s.instagram],
    ["Facebook", s.facebook && "Find us on Facebook", s.facebook],
    ["YouTube", s.youtube && "Watch our films", s.youtube],
    ["Based in", c.location, null],
  ].filter(([, val]) => val);

  return (
    <>
      <PageHero
        eyebrow="Contact / Request a Quote"
        testid="contact-title"
        title={<>Let's Create Something <span className="italic">Beautiful.</span></>}
        text="Tell us about your brand and what you have in mind. We'd love to explore how we can bring your vision to life."
      />
      <section className="mx-auto max-w-[1400px] px-6 pb-32 md:px-10 md:pb-44">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-10">
          <aside className="lg:col-span-3">
            <dl className="space-y-8" data-testid="contact-details">
              {details.map(([label, val, href]) => (
                <div key={label}>
                  <dt className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-ink">{label}</dt>
                  <dd className="mt-2 break-words font-serif text-xl text-wine">
                    {href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="hover:underline">{val}</a> : val}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-12 max-w-xs text-sm leading-relaxed text-muted-ink">Every enquiry is read personally. Working with brands in India and internationally.</p>
          </aside>
          <div className="lg:col-span-8 lg:col-start-5">{done ? <Success /> : <QuoteForm onSuccess={() => setDone(true)} />}</div>
        </div>
      </section>
    </>
  );
}
